import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import { useTheme } from '@mui/material';
import { inter } from '../../styles/fonts';

interface StepShellProps {
  stepIdx: number;
  total: number;
  mobile: boolean;
  onBack?: (() => void) | null;
  onSkip?: (() => void) | null;
  footer: React.ReactNode;
  headerRight?: React.ReactNode;
  children: React.ReactNode;
}

export default function StepShell({
  stepIdx,
  total,
  mobile,
  onBack,
  onSkip,
  footer,
  headerRight,
  children,
}: StepShellProps) {
  const theme = useTheme();
  const padX = mobile ? 20 : 0;
  const pct = ((stepIdx + 1) / total) * 100;
  const footerRef = useRef<HTMLDivElement>(null);
  const [footerHeight, setFooterHeight] = useState(110);

  useEffect(() => {
    if (!mobile || !footerRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      setFooterHeight(entry.contentRect.height);
    });
    observer.observe(footerRef.current);
    return () => observer.disconnect();
  }, [mobile]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          flexShrink: 0,
          px: `${padX}px`,
          pt: mobile ? '0px' : '28px',
          pb: '14px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: '14px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              minWidth: 0,
            }}
          >
            {onBack ? (
              <Box
                component="button"
                onClick={onBack}
                aria-label="Back"
                sx={{
                  fontFamily: 'inherit',
                  fontSize: 18,
                  background: 'transparent',
                  border: 0,
                  color: theme.palette.text.secondary,
                  cursor: 'pointer',
                  p: '4px 6px',
                  ml: '-6px',
                }}
              >
                ←
              </Box>
            ) : null}
          </Box>
          {onSkip ? (
            <Box
              component="button"
              onClick={onSkip}
              sx={{
                fontFamily: 'inherit',
                fontWeight: 600,
                fontSize: 13,
                color: theme.palette.text.secondary,
                background: 'transparent',
                border: 0,
                cursor: 'pointer',
                p: '6px 0',
                whiteSpace: 'nowrap',
              }}
            >
              Skip to menu →
            </Box>
          ) : null}
        </Box>

        {/* Progress */}
        <Box>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', mb: '6px' }}
          >
            <Box
              component="span"
              sx={{
                fontFamily: inter.style.fontFamily,
                fontWeight: 600,
                fontSize: 11,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: theme.palette.text.secondary,
              }}
              aria-live="polite"
            >
              Step {stepIdx + 1} of {total}
            </Box>
          </Box>
          <LinearProgress
            variant="determinate"
            value={pct}
            role="progressbar"
            aria-valuenow={stepIdx + 1}
            aria-valuemin={1}
            aria-valuemax={total}
            sx={{
              height: 4,
              borderRadius: 999,
              backgroundColor: 'rgba(4,28,44,0.08)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: theme.palette.primary.main,
                borderRadius: 999,
                transition: 'transform 240ms cubic-bezier(0.2, 0.8, 0.2, 1)',
              },
            }}
          />
        </Box>

        {headerRight}
      </Box>

      {/* Scrollable body */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          px: `${padX}px`,
          pt: '8px',
          pb: mobile ? `${footerHeight}px` : 0,
        }}
      >
        {children}
      </Box>

      {/* Footer */}
      {footer && (
        <Box
          ref={footerRef}
          sx={{
            flexShrink: 0,
            px: `${padX}px`,
            pt: '14px',
            pb: mobile ? '28px' : '24px',
            borderTop: '1px solid rgba(4,28,44,0.25)',
            ...(mobile && {
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 10,
              backgroundColor: 'background.paper',
              borderTopLeftRadius: '16px',
              borderTopRightRadius: '16px',
            }),
          }}
        >
          {footer}
        </Box>
      )}
    </Box>
  );
}

interface PrimaryBtnProps {
  onClick?: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  fullWidth?: boolean;
}

export function PrimaryBtn({
  onClick,
  disabled,
  children,
  fullWidth = true,
}: PrimaryBtnProps) {
  const theme = useTheme();
  return (
    <Box
      component="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      sx={{
        width: fullWidth ? '100%' : 'auto',
        fontFamily: 'inherit',
        fontWeight: 600,
        fontSize: 16,
        px: '22px',
        py: '14px',
        borderRadius: '10px',
        background: disabled ? '#E5E5EE' : theme.palette.primary.main,
        color: disabled ? '#9999A8' : theme.palette.primary.contrastText,
        border: 0,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'opacity 150ms cubic-bezier(0.2, 0.8, 0.2, 1)',
        '&:hover': { opacity: disabled ? 1 : 0.88 },
      }}
    >
      {children}
    </Box>
  );
}

interface SelectionChipProps {
  label: string;
  selected?: boolean;
  fromTemplate?: boolean;
  onClick?: () => void;
  size?: 'md' | 'lg';
}

export function SelectionChip({
  label,
  selected,
  fromTemplate,
  onClick,
  size = 'md',
}: SelectionChipProps) {
  const theme = useTheme();
  const padY = size === 'lg' ? 10 : 7;
  const padX = size === 'lg' ? 16 : 12;
  const fontSize = size === 'lg' ? 15 : 13;
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        fontFamily: 'inherit',
        fontWeight: 500,
        fontSize,
        lineHeight: 1.2,
        px: `${padX}px`,
        py: `${padY}px`,
        borderRadius: 999,
        background: selected ? theme.palette.primary.main : '#FFFFFF',
        color: selected ? theme.palette.primary.contrastText : '#041C2C',
        border: selected
          ? `1.5px solid ${theme.palette.primary.main}`
          : '1.5px solid #E5E5EE',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        transition: 'all 120ms cubic-bezier(0.2, 0.8, 0.2, 1)',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
      {fromTemplate && (
        <Box
          component="span"
          sx={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            px: '6px',
            py: '2px',
            borderRadius: 999,
            background: 'rgba(4,28,44,0.08)',
            color: theme.palette.primary.contrastText,
          }}
        >
          From template
        </Box>
      )}
    </Box>
  );
}
